import gql from 'graphql-tag';
import { getGraphQLClientWithToken } from 'utils/api';
import {
  AUTO_COLUMN_KEY,
  GROUP_ID_KEY,
  CHILDREN_KEY,
  RULE_COLUMNS,
  RULE_ID,
  RULE_KEY,
  CONDITIONS,
} from './metadata';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { IDataSourceProps } from '..';
import { generateFilters, getFilteredNodes, getSortedNodes } from './utils';

// inspired by https://www.ag-grid.com/graphql-server-side-operations/
export const createServerSideDatasource = ({
  parentColumns,
  childrenColumns,
  groupColumnKey,
  gqlMethod,
  sortingType,
  filterType,
}: IDataSourceProps) => {
  const convertSortModel = (sortModel: any) => {
    const parentSortModel = sortModel.filter(({ colId }: any) => {
      return (
        Object.values(parentColumns).includes(colId) ||
        colId === AUTO_COLUMN_KEY
      );
    });

    const res = parentSortModel.reduce((acc: any, { colId, sort }: any) => {
      const key = colId === AUTO_COLUMN_KEY ? groupColumnKey : colId;
      return { ...acc, [key]: `${sort}`.toUpperCase() };
    }, {});

    return !isEmpty(res) ? res : { [groupColumnKey]: 'ASC' };
  };

  const convertFilterModel = (filterModel: any) => {
    if (!filterModel || Object.keys(filterModel).length === 0) {
      return;
    }

    const parentFilterModel = Object.keys(filterModel).reduce((acc, key) => {
      return {
        ...acc,
        ...(key === AUTO_COLUMN_KEY ||
        (Object.values(parentColumns).includes(key) &&
          !Object.values(childrenColumns).includes(key))
          ? { [key]: filterModel[key] }
          : {}),
      };
    }, {});
    const childrenFilterModel = Object.keys(filterModel).reduce((acc, key) => {
      return {
        ...acc,
        ...(Object.values(childrenColumns).includes(key)
          ? { [key]: filterModel[key] }
          : {}),
      };
    }, {});

    const result = {
      AND: [
        ...generateFilters(parentFilterModel, groupColumnKey),
        ...(!isEmpty(childrenFilterModel)
          ? [
              {
                [`${CHILDREN_KEY}_${CONDITIONS.some}`]: {
                  AND: generateFilters(childrenFilterModel, groupColumnKey),
                },
              },
            ]
          : []),
      ],
    };

    return result;
  };

  // Extract(flatten) rule and generate group identifier 'path'
  const getFormattedNodes = (nodes: any) => {
    return nodes.map((node: any) => {
      const { [RULE_KEY]: rule } = node;
      const { [RULE_COLUMNS.id]: ruleId = 0, ...restRule } = rule || {};
      // Generate group key by GROUP BY values
      const path = Object.values(parentColumns)
        .filter((item) => !Object.values(childrenColumns).includes(item))
        .map((columnKey) => node[columnKey])
        .join(',');

      return {
        ...node,
        ...restRule,
        [RULE_ID]: ruleId,
        [GROUP_ID_KEY]: path,
      };
    });
  };

  // Apply filters to children
  const getNodesWithFilteredChildren = (
    nodes: any,
    { filterModel, sortModel }: any
  ) => {
    const childrenFilterModel = Object.keys(filterModel).reduce((acc, key) => {
      return {
        ...acc,
        ...(Object.values(childrenColumns).includes(key)
          ? { [key]: filterModel[key] }
          : {}),
      };
    }, {});
    const childrenSortModel = sortModel.filter((item: any) => {
      return Object.values(childrenColumns).includes(item.colId);
    });

    return getSortedNodes(
      getFilteredNodes(nodes, childrenFilterModel),
      childrenSortModel
    );
  };

  const getChildren = (parentNode: any) => {
    const {
      [CHILDREN_KEY]: children,
      [GROUP_ID_KEY]: path,
      [RULE_KEY]: rule,
      ...nodeData
    } = parentNode;

    return parentNode[CHILDREN_KEY].map((item: any) => ({
      ...nodeData,
      ...item,
    }));
  };

  const query = (request: any, totalCount: number) => {
    const parentFields = Object.values(parentColumns).join(' ');
    const childrenFields = Object.values(childrenColumns).join(' ');
    const ruleFields = Object.values(RULE_COLUMNS).join(' ');
    const { startRow, endRow: fromItemsCount } = request;
    const pageItemsCount = fromItemsCount - startRow;
    // From {first} amount of items get last {last} items
    // totalCount equals 1 on first load (page)
    // Handle last page params
    const first =
      totalCount > 1
        ? totalCount <= fromItemsCount
          ? totalCount
          : fromItemsCount
        : fromItemsCount;
    const last =
      totalCount > 1
        ? totalCount <= fromItemsCount
          ? totalCount - startRow
          : pageItemsCount
        : pageItemsCount;

    return {
      fetchPolicy: 'cache-and-network',
      query: gql`
			query (
			  $first: Int
        $last: Int
        $sorting: ${sortingType}
        $filter: ${filterType}
			)
			{
				${gqlMethod} (
					first: $first
          last: $last
          order_by: $sorting
          where: $filter
				 )
				{
          nodes {
            ${parentFields}
            ${CHILDREN_KEY}{
              ${childrenFields}
            }
            ${RULE_KEY}{
              ${ruleFields}
            }
          }
					totalCount
					pageInfo
					{
						hasNextPage,
						hasPreviousPage
					}
				}
			}
			`,
      variables: {
        first,
        last,
        sorting: convertSortModel(request.sortModel),
        filter: convertFilterModel(request.filterModel),
      },
    };
  };

  let client = null as any;
  let total = 1;

  return {
    getRows: async (params: any) => {
      const { request } = params;
      const isParentNode = request.groupKeys.length === 0;

      if (!isParentNode) {
        const rows = getChildren(params?.parentNode?.data);
        const rowCount = rows.length;

        return params.successCallback(rows, rowCount);
      }

      client = client || (await getGraphQLClientWithToken());
      total = params?.parentNode?.childrenCache?.displayIndexEnd || 1;

      client.watchQuery(query(request, total)).subscribe({
        next(response: any) {
          if (get(response, `data.${gqlMethod}`)) {
            const { nodes, totalCount } = response.data[gqlMethod];
            const rows = getFormattedNodes(
              getNodesWithFilteredChildren(nodes, request)
            );

            params.successCallback(rows, totalCount);
          }
        },
        error(err: any) {
          params.failCallback(err);
        },
      });
    },
  };
};
